import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { Avatar, AvatarGroup, Flex, Image } from '@chakra-ui/react';

import folderImg from 'assets/brand/folder.png';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import { logEvent } from 'features/Analytics/analytics';
import TrackView from 'features/Analytics/TrackView';
import * as Layout from 'layouts/ContentLayout';

import * as routes from '../../routes';
import teamPicture1 from './assets/1.png';
import teamPicture2 from './assets/2.png';
import teamPicture3 from './assets/3.png';
import HelpQuestionSidebar from './components/HelpQuestionSidebar';
import HelpUrgentSidebar from './components/HelpUrgentSidebar';
import useSubscriptionPlanProfile from './hooks/useSubscriptionPlanProfile';
import locales from './locales';

const teamPictures = [teamPicture1, teamPicture2, teamPicture3];

const HelpHome: FC = () => {
  const [sidebarMode, setSidebarMode] = useState<'question' | 'urgent' | null>(
    null,
  );

  const { canAccessPhoneSupport, loading } = useSubscriptionPlanProfile();

  const intercom = useIntercom();

  if (loading) {
    return <Loader />;
  }

  return (
    <Layout.Root>
      <TrackView pageName="Help" />

      <Layout.Body>
        <Flex flexDirection="column" gap={8} marginBottom="space-40">
          <Heading size="lg">{locales.title}</Heading>
          <Text size="lg" variant="secondary">
            {locales.subtitle}
          </Text>
        </Flex>

        <SunshineCard.Group marginBottom="space-32">
          <SunshineCard
            alignItems="center"
            as="a"
            href="https://help.shine.fr/fr/"
            onClick={() => logEvent({ name: 'FAQ Help Menu' })}
            rel="noopener noreferrer"
            target="_blank"
          >
            <SunshineCard.Content>
              <SunshineCard.Title size="lg">
                {locales.FAQ.title}
              </SunshineCard.Title>
              <SunshineCard.Text>{locales.FAQ.description}</SunshineCard.Text>
              <SunshineCard.Button
                icon="search"
                iconPosition="left"
                marginTop="space-16"
                variant="primary"
              >
                {locales.FAQ.search}
              </SunshineCard.Button>
            </SunshineCard.Content>

            <SunshineCard.Slot name="action">
              <Image boxSize="120px" src={folderImg} />
            </SunshineCard.Slot>
          </SunshineCard>
        </SunshineCard.Group>

        <SunshineCard.Group marginBottom="space-16">
          <SunshineCard alignItems="center">
            <SunshineCard.Content>
              <SunshineCard.Title size="lg">
                {locales.team.title}
              </SunshineCard.Title>
              <SunshineCard.Text>{locales.team.description}</SunshineCard.Text>
            </SunshineCard.Content>
            <SunshineCard.Slot name="action">
              <AvatarGroup spacing="-space-16">
                {teamPictures.map((picture) => (
                  <Avatar
                    height="space-48"
                    key={picture}
                    src={picture}
                    width="space-48"
                  />
                ))}
              </AvatarGroup>
            </SunshineCard.Slot>
          </SunshineCard>

          {canAccessPhoneSupport ? (
            <SunshineCard
              alignItems="center"
              isActive={sidebarMode === 'question'}
              onClick={() => setSidebarMode('question')}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="question" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Title>{locales.Email.title}</SunshineCard.Title>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          ) : (
            <SunshineCard
              alignItems="center"
              as={Link}
              onClick={() => logEvent({ name: 'Ask Question Help Menu' })}
              to={routes.askQuestion}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="mail" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Title>{locales.Email.title}</SunshineCard.Title>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineTag icon="timer" noOfLines={1} size="sm">
                  {locales.Email.tag}
                </SunshineTag>
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          )}

          {canAccessPhoneSupport ? (
            <SunshineCard
              alignItems="center"
              isActive={sidebarMode === 'urgent'}
              onClick={() => setSidebarMode('urgent')}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="bell" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Title>{locales.Chat.title}</SunshineCard.Title>
                <SunshineCard.Text>{locales.Chat.subtitle}</SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          ) : (
            <SunshineCard
              alignItems="center"
              id="help-menu-chat"
              onClick={() => logEvent({ name: 'Chat Support Help Menu' })}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="bell" />
              </SunshineCard.Slot>

              <SunshineCard.Content>
                <SunshineCard.Title>{locales.Chat.title}</SunshineCard.Title>
                <SunshineCard.Text>{locales.Chat.subtitle}</SunshineCard.Text>
              </SunshineCard.Content>

              <SunshineCard.Slot name="action">
                <SunshineTag icon="timer" noOfLines={1} size="sm">
                  {locales.Chat.tag}
                </SunshineTag>
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          )}

          <SunshineCard
            alignItems="center"
            onClick={() => {
              setSidebarMode(null);
              intercom.showMessages();
            }}
          >
            <SunshineCard.Slot name="aside">
              <SquircleIcon name="history" />
            </SunshineCard.Slot>

            <SunshineCard.Content>
              <SunshineCard.Title>{locales.conversations}</SunshineCard.Title>
            </SunshineCard.Content>

            <SunshineCard.Slot name="action">
              <SunshineCard.SunshineIcon name="chevron-right" />
            </SunshineCard.Slot>
          </SunshineCard>
        </SunshineCard.Group>
        {canAccessPhoneSupport ? null : (
          <Text size="sm" variant="light">
            {locales.businessHours}
          </Text>
        )}
      </Layout.Body>
      {sidebarMode === 'question' ? (
        <HelpQuestionSidebar onClose={() => setSidebarMode(null)} />
      ) : null}
      {sidebarMode === 'urgent' ? (
        <HelpUrgentSidebar onClose={() => setSidebarMode(null)} />
      ) : null}
    </Layout.Root>
  );
};

export default HelpHome;
